import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { successNotifier } from "../../../components/NotificationHandler";
import { AuthContext } from "../../../context/authContext";
import { COLORS } from "../../../constants/colors";
import PaystackModal from "../../idCard/components/PaystackModal";
import { payVendorProduct } from "../service";

const getNumericPrice = (price) => {
  const amount = Number.parseFloat(String(price || "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (amount, fallback = "N/A") => {
  if (!Number.isFinite(amount)) return fallback;

  return `NGN ${amount.toLocaleString("en-NG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const DetailRow = ({ label, value }) => (
  <Flex justify="space-between" gap="4" fontSize="sm">
    <Text color="#707070">{label}</Text>
    <Text fontWeight="600" textAlign="right">
      {value || "N/A"}
    </Text>
  </Flex>
);

const FoodCard = ({ item, showVendor = true }) => {
  const cardBg = useColorModeValue(COLORS.white, COLORS.grey);
  const appTextColor = useColorModeValue(COLORS.primary, COLORS.lightGrey);
  const mutedColor = useColorModeValue("#707070", COLORS.lightGrey);
  const hoverBg = useColorModeValue("#f5f5f5", "#2D3748");
  const buttonBg = useColorModeValue(COLORS.primary, COLORS.secondary);
  const vendorBg = useColorModeValue("#f9f9f9", "#1f1f2e");
  const quantityBorderColor = useColorModeValue("#e2e8f0", "#4a5568");

  const [quantity, setQuantity] = useState(1);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({});
  const [paymentModal, setPaymentModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    userInfo: { userId },
  } = useContext(AuthContext);
  const navigate = useNavigate();

  const availability = String(item?.availability || "Available");
  const normalizedAvailability = availability.toLowerCase();
  const isUnavailable =
    normalizedAvailability.includes("unavailable") ||
    normalizedAvailability.includes("out of stock") ||
    normalizedAvailability.includes("inactive") ||
    normalizedAvailability.includes("not available");
  const isAvailable = !isUnavailable;
  const unitPrice = getNumericPrice(item?.amount ?? item?.price);
  const totalAmount = unitPrice ? unitPrice * quantity : null;
  const vendorProductId = item?.vendorProductId || item?.id;
  const images = item?.images?.length ? item.images : item?.image ? [item.image] : [];
  const currentImage = images[currentImageIndex] || images[0];
  const hasMultipleImages = images.length > 1;

  const handleIncrement = () => setQuantity(quantity + 1);
  const handleDecrement = () => setQuantity(quantity > 1 ? quantity - 1 : 1);
  const showPreviousImage = () =>
    setCurrentImageIndex((index) => (index === 0 ? images.length - 1 : index - 1));
  const showNextImage = () =>
    setCurrentImageIndex((index) => (index === images.length - 1 ? 0 : index + 1));

  const handleViewVendor = (e) => {
    e.stopPropagation();
    navigate(`/vendor-foods/${encodeURIComponent(item.vendorName)}`);
  };

  const handleProceedToPayment = async () => {
    const payload = {
      userId,
      vendorProductId,
      vendorId: item.vendorId,
      productName: item.name,
      vendorName: item.vendorName,
      description: item.description,
      amount: unitPrice,
      quantity,
      imageUrl: images[0] || item.image,
      imageUrls: images,
    };

    const data = await payVendorProduct(payload, setPaymentLoading);
    const paystackData =
      data?.result?.data?.paystack ||
      data?.data?.paystack ||
      data?.paystack;

    if (paystackData?.authorization_url) {
      setPaymentData(paystackData);
      setPaymentModal(true);
      onClose();
      return;
    }

    if (data) onClose();
  };

  return (
    <>
      <Box
        bg={cardBg}
        borderRadius="10px"
        overflow="hidden"
        shadow="sm"
        _hover={{ shadow: "md", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        {currentImage ? (
          <Box position="relative" h="132px">
            <Image src={currentImage} alt={item.name} w="100%" h="132px" objectFit="cover" />
            {hasMultipleImages ? (
              <>
                <Button
                  position="absolute"
                  left="2"
                  top="50%"
                  transform="translateY(-50%)"
                  size="xs"
                  minW="7"
                  h="7"
                  borderRadius="full"
                  bg="rgba(0,0,0,0.45)"
                  color={COLORS.white}
                  onClick={showPreviousImage}
                  _hover={{ bg: "rgba(0,0,0,0.6)" }}
                >
                  <BsChevronLeft />
                </Button>
                <Button
                  position="absolute"
                  right="2"
                  top="50%"
                  transform="translateY(-50%)"
                  size="xs"
                  minW="7"
                  h="7"
                  borderRadius="full"
                  bg="rgba(0,0,0,0.45)"
                  color={COLORS.white}
                  onClick={showNextImage}
                  _hover={{ bg: "rgba(0,0,0,0.6)" }}
                >
                  <BsChevronRight />
                </Button>
                <Flex
                  position="absolute"
                  bottom="2"
                  left="0"
                  right="0"
                  justify="center"
                  gap="1"
                >
                  {images.map((image, index) => (
                    <Box
                      key={`${image}-${index}`}
                      w="1.5"
                      h="1.5"
                      borderRadius="full"
                      bg={index === currentImageIndex ? COLORS.white : "rgba(255,255,255,0.5)"}
                    />
                  ))}
                </Flex>
              </>
            ) : null}
          </Box>
        ) : (
          <Flex
            w="100%"
            h="132px"
            align="center"
            justify="center"
            bg={`linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.lightGrey} 100%)`}
            color={COLORS.white}
            fontWeight="600"
            textAlign="center"
            p="4"
          >
            No image available
          </Flex>
        )}

        <Stack p="3" spacing="2">
          <Flex justify="space-between" align="center" gap="2">
            <Text fontWeight="700" color={appTextColor} noOfLines={1} flex="1">
              {item.name}
            </Text>
            <Badge
              colorScheme={isAvailable ? "green" : "red"}
              borderRadius="999px"
              px="2"
              py="1"
              whiteSpace="nowrap"
            >
              {availability}
            </Badge>
          </Flex>

          <Text fontSize="xs" color={mutedColor} noOfLines={1}>
            {item.description || "No description provided."}
          </Text>

          <Flex justify="space-between" gap="2" fontSize="xs" color={appTextColor}>
            <Text fontWeight="700" noOfLines={1}>
              {item.price}
            </Text>
            <Text noOfLines={1} textAlign="right">
              {item.category}
            </Text>
          </Flex>

          <Text fontSize="xs" color={mutedColor} noOfLines={1}>
            {item.location}
          </Text>

          {showVendor ? (
            <Flex
              align="center"
              gap="2"
              px="2"
              py="1"
              bg={vendorBg}
              borderRadius="6px"
              cursor="pointer"
              _hover={{ bg: hoverBg }}
              onClick={handleViewVendor}
              transition="bg 0.2s"
            >
              <Avatar size="xs" name={item.vendorName} bg={COLORS.primary} color={COLORS.white} />
              <Text fontSize="xs" fontWeight="600" color={appTextColor} noOfLines={1}>
                {item.vendorName}
              </Text>
            </Flex>
          ) : null}

          <Flex gap="2" align="center" minW="0">
            <HStack
              border="1px solid"
              borderColor={quantityBorderColor}
              borderRadius="6px"
              spacing="0"
              flexShrink="0"
            >
              <Button
                size="xs"
                variant="ghost"
                onClick={handleDecrement}
                _hover={{ bg: "transparent" }}
                p="0"
                minW="7"
                h="7"
              >
                -
              </Button>
              <Box
                w="7"
                textAlign="center"
                borderLeft="1px solid"
                borderRight="1px solid"
                borderColor={quantityBorderColor}
              >
                <Text fontSize="xs" fontWeight="600">
                  {quantity}
                </Text>
              </Box>
              <Button
                size="xs"
                variant="ghost"
                onClick={handleIncrement}
                _hover={{ bg: "transparent" }}
                p="0"
                minW="7"
                h="7"
              >
                +
              </Button>
            </HStack>

            <Button
              flex="1"
              minW="0"
              size="xs"
              bg={buttonBg}
              color={COLORS.white}
              onClick={onOpen}
              isDisabled={!isAvailable}
              _hover={{ opacity: 0.9 }}
              fontWeight="600"
              fontSize="xs"
              px="2"
            >
              Order
            </Button>
          </Flex>
        </Stack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={appTextColor} fontSize="lg">
            Order Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              {currentImage ? (
                <Box position="relative" h="160px" borderRadius="8px" overflow="hidden">
                  <Image
                    src={currentImage}
                    alt={item.name}
                    w="100%"
                    h="160px"
                    objectFit="cover"
                  />
                  {hasMultipleImages ? (
                    <>
                      <Button
                        position="absolute"
                        left="2"
                        top="50%"
                        transform="translateY(-50%)"
                        size="xs"
                        minW="7"
                        h="7"
                        borderRadius="full"
                        bg="rgba(0,0,0,0.45)"
                        color={COLORS.white}
                        onClick={showPreviousImage}
                        _hover={{ bg: "rgba(0,0,0,0.6)" }}
                      >
                        <BsChevronLeft />
                      </Button>
                      <Button
                        position="absolute"
                        right="2"
                        top="50%"
                        transform="translateY(-50%)"
                        size="xs"
                        minW="7"
                        h="7"
                        borderRadius="full"
                        bg="rgba(0,0,0,0.45)"
                        color={COLORS.white}
                        onClick={showNextImage}
                        _hover={{ bg: "rgba(0,0,0,0.6)" }}
                      >
                        <BsChevronRight />
                      </Button>
                      <Flex
                        position="absolute"
                        bottom="2"
                        left="0"
                        right="0"
                        justify="center"
                        gap="1"
                      >
                        {images.map((image, index) => (
                          <Box
                            key={`modal-${image}-${index}`}
                            w="1.5"
                            h="1.5"
                            borderRadius="full"
                            bg={
                              index === currentImageIndex
                                ? COLORS.white
                                : "rgba(255,255,255,0.5)"
                            }
                          />
                        ))}
                      </Flex>
                    </>
                  ) : null}
                </Box>
              ) : null}

              <Stack spacing="1">
                <Flex justify="space-between" align="start" gap="3">
                  <Text fontWeight="700" color={appTextColor}>
                    {item.name}
                  </Text>
                  <Badge colorScheme={isAvailable ? "green" : "red"} borderRadius="999px">
                    {availability}
                  </Badge>
                </Flex>
                <Text fontSize="sm" color={mutedColor}>
                  {item.description || "No description provided."}
                </Text>
              </Stack>

              <Divider />

              <Stack spacing="2" color={appTextColor}>
                <DetailRow label="Vendor" value={item.vendorName} />
                <DetailRow label="Category" value={item.category} />
                <DetailRow label="Location" value={item.location} />
                <DetailRow label="Unit price" value={item.price} />
                <DetailRow label="Quantity" value={quantity} />
                <DetailRow label="Total" value={formatAmount(totalAmount, item.price)} />
              </Stack>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              w="100%"
              bg={buttonBg}
              color={COLORS.white}
              onClick={handleProceedToPayment}
              isLoading={paymentLoading}
              isDisabled={!userId || !vendorProductId || !item.vendorId || unitPrice === null}
              _hover={{ opacity: 0.9 }}
            >
              Proceed to Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <PaystackModal
        paymentData={paymentData}
        paymentModal={paymentModal}
        setPaymentModal={setPaymentModal}
        onFinish={() => {
          setPaymentModal(false);
          successNotifier("Payment completed successfully!");
        }}
      />
    </>
  );
};

export default FoodCard;
